const MET_ENTRIES = [
  ["running", 9.8],
  ["jogging", 7],
  ["walking", 3.5],
  ["cycling", 7.5],
  ["biking", 7.5],
  ["swimming", 8],
  ["weightlifting", 6],
  ["lifting", 6],
  ["yoga", 3],
  ["hiit", 8],
  ["pilates", 3.5],
  ["dancing", 5],
  ["hiking", 6],
  ["rowing", 8.5],
  ["elliptical", 5],
  ["jumping", 8],
  ["skipping", 8],
  ["soccer", 7],
  ["football", 7],
  ["basketball", 6.5],
  ["tennis", 7.3],
  ["climbing", 8],
  ["skiing", 7],
] as const;

export const DEFAULT_BODY_WEIGHT_KG = 70;
const DEFAULT_MET = 6;

export type WorkoutFormState = {
  readonly type: string;
  readonly duration: string;
  readonly distance: string;
  readonly reps: string;
  readonly sets: string;
  readonly weight: string;
  readonly met: string;
  readonly notes: string;
};

type WorkoutMetricField = "distanceM" | "reps" | "sets" | "weightKg";

type ParsedMetrics = Partial<Record<WorkoutMetricField, number>>;

export const EMPTY_FORM: WorkoutFormState = {
  type: "",
  duration: "",
  distance: "",
  reps: "",
  sets: "",
  weight: "",
  met: "",
  notes: "",
};

export function parseNumber(value: string): number | undefined {
  if (value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function lookupMet(type: string): number {
  const normalized = type.trim().toLowerCase();
  const exact = MET_ENTRIES.find(([name]) => name === normalized);
  if (exact !== undefined) return exact[1];
  const partial = MET_ENTRIES.find(([name]) => normalized.includes(name));
  return partial?.[1] ?? DEFAULT_MET;
}

export function estimateKcal(met: number, weightKg: number, durationMin: number): number {
  return Math.round((met * 3.5 * weightKg * durationMin) / 200);
}

export function getErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function parseMetrics(form: WorkoutFormState):
  | { readonly ok: true; readonly metrics: ParsedMetrics }
  | { readonly ok: false; readonly message: string } {
  const metricFields = [
    { key: "distanceM", label: "Distance", value: form.distance },
    { key: "reps", label: "Reps", value: form.reps },
    { key: "sets", label: "Sets", value: form.sets },
    { key: "weightKg", label: "Weight", value: form.weight },
  ] as const;
  const metrics: ParsedMetrics = {};
  for (const field of metricFields) {
    if (field.value.trim() === "") continue;
    const value = parseNumber(field.value);
    if (value === undefined || value < 0) {
      return { ok: false, message: `${field.label} must be a non-negative number.` };
    }
    metrics[field.key] = field.key === "reps" || field.key === "sets" ? Math.round(value) : value;
  }
  return { ok: true, metrics };
}
